import React, { useState, useEffect } from 'react';
import { BookOpen } from 'lucide-react';
import AdminCRUD from '../../components/admin/AdminCRUD';
import api from '../../utils/api';

const STATUS_STYLE = { active:'bg-emerald-100 text-emerald-700', completed:'bg-blue-100 text-blue-700', upcoming:'bg-amber-100 text-amber-700', 'on-hold':'bg-gray-100 text-gray-600' };

export default function AdminPrograms() {
  const [categories, setCategories] = useState([]);
  const [catLoaded, setCatLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // admin/all is paginated ({ items, total, ... }); default limit is 15,
        // so pass a high limit here since we want the full list for the dropdown.
        const res = await api.get('/program-categories/admin/all?limit=200');
        setCategories(res.data.items || []);
      } catch (e) {
        console.error(e);
      } finally {
        setCatLoaded(true);
      }
    })();
  }, []);

  const categoryLabel = (slug) => categories.find(c => c.slug === slug)?.name || slug;

  // Don't render the CRUD table (and its form) until categories have loaded,
  // so the category <select> options are correct from the first render.
  if (!catLoaded) return null;

  return (
    <AdminCRUD
      endpoint="programs"
      title="Programs"
      icon={BookOpen}
      columns={[
        { key:'title', label:'Program', render:(v,item) => (
          <div className="flex items-center gap-3">
            {item.image
              ? <img src={item.thumbImage||item.image} alt={v} className="w-11 h-11 rounded-xl object-cover flex-shrink-0" />
              : <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-royal-700 to-royal-900 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{v?.[0]}</div>}
            <div>
              <div className="font-semibold text-gray-900 text-sm">{v}</div>
              <span className="px-2 py-0.5 bg-royal-50 text-royal-700 rounded-full text-[10px] font-semibold capitalize">{categoryLabel(item.category)}</span>
            </div>
          </div>
        )},
        { key:'location', label:'Location', render: v => <span className="text-sm text-gray-500">{v || 'Nationwide'}</span> },
        { key:'status', label:'Status', render: v => <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLE[v]||'bg-gray-100 text-gray-600'}`}>{v}</span> },
        { key:'beneficiaries', label:'Beneficiaries', render: v => v ? <span className="text-sm font-medium">{v.toLocaleString()}+</span> : '—' },
        { key:'isPublished', label:'Live', render: v => v ? <span className="text-emerald-500 font-bold">✓</span> : <span className="text-gray-300">—</span> },
      ]}
      defaultForm={{ title:'', category: categories[0]?.slug || '', description:'', shortDescription:'', image:'', location:'', status:'active', beneficiaries:'', isPublished:true }}
      formFields={[
        { key:'image', label:'Program Image', type:'image', fullWidth:true },
        { key:'title', label:'Program Title', required:true, fullWidth:true },
        { key:'category', label:'Category', type:'select', options: categories.map(c => ({ value: c.slug, label: c.name })) },
        { key:'status', label:'Status', type:'select', options:['active','upcoming','completed','on-hold'] },
        { key:'location', label:'Location / State' },
        { key:'beneficiaries', label:'Beneficiaries Count', type:'number' },
        { key:'shortDescription', label:'Short Description (card preview)', type:'textarea', fullWidth:true, rows:2 },
        { key:'description', label:'Full Description', type:'textarea', fullWidth:true, rows:6, required:true },
        { key:'startDate', label:'Start Date', type:'date' },
        { key:'endDate', label:'End Date', type:'date' },
        { key:'isPublished', label:'Publish', type:'checkbox', checkLabel:'Show on public website', fullWidth:true },
        { key:'isFeatured', label:'Feature', type:'checkbox', checkLabel:'Feature on homepage', fullWidth:true },
      ]}
    />
  );
}